import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import MarketsLayout from '@fuww/library/src/Markets';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import getBrandsBreadcrumbs from './getBreadcrumbs';
import messages from '../../lib/messages.json';
import marketQuery from '../../lib/queries/products/marketQuery.graphql';
import throw404 from '../../lib/throw404';

type MarketHeadingProperties = Omit<HeadingProperties, 'marketId'> & {
  data: {
    market: { id: number; name: string; description: string } | null;
  };
};

const MarketHeading = withQuery(({
  data: { market },
  tabs,
  children,
}: MarketHeadingProperties) => {
  const intl = useIntl();

  if (!market) {
    return throw404();
  }

  const { id: marketId, name, description } = market;

  return (
    <>
      <Head
        title={intl.formatMessage(messages['brands.title'], { name })}
        description={description}
        subjectType="Market"
        subjectId={marketId}
      />
      <MarketsLayout
        title={intl.formatMessage(messages['brands.headingTitle'], { name })}
        description={description}
        breadcrumbs={getBrandsBreadcrumbs({ marketName: name, marketId })}
        tabs={tabs}
      >
        {children}
      </MarketsLayout>
    </>
  );
});

const headingPropertyTypes = {
  marketId: PropTypes.number,
  tabs: PropTypes.node,
  children: PropTypes.node,
};

type HeadingProperties = InferProps<typeof headingPropertyTypes>;

const Heading = ({ marketId, children, tabs }: HeadingProperties) => (
  <MarketHeading
    query={marketQuery}
    variables={{ id: marketId }}
    errorMessage="Error loading market information"
    tabs={tabs}
  >
    {children}
  </MarketHeading>
);

Heading.propTypes = headingPropertyTypes;

export default Heading;
